import API_ENDPOINTS from "@/common/api/apiEndpoints";
import httpClient from "@/common/api/httpClient";
import httpMethods from "@/common/api/httpMethods";

// Users API

export const fetchUserDetailsApi = () => {
  return httpClient(httpMethods.GET, API_ENDPOINTS.AUTH.useDetails);
};

export const fetchUser = (userId: number) => {
  const endpoint = `${API_ENDPOINTS.AUTH.user}${encodeURIComponent(userId)}`;
  return httpClient(httpMethods.GET, endpoint);
};

export const fetchUserPreferenceApi = () => {
  return httpClient(httpMethods.GET, API_ENDPOINTS.AUTH.usePreference);
};

export const updateUserPreferenceApi = (payload: {}) => {
  return httpClient(httpMethods.PUT, API_ENDPOINTS.AUTH.usePreference, payload);
};

export const fetchUserSessionApi = () => {
  return httpClient(httpMethods.GET, API_ENDPOINTS.AUTH.sessions, undefined, {
    skipAuthRedirect: true,
  });
};

export const fetchWorkspaceDetails = (versionId: string) => {
  const endpoint = `${API_ENDPOINTS.WORKSPACE.workspaceVersions}${encodeURIComponent(versionId)}`;
  return httpClient(httpMethods.GET, endpoint);
};

export const checkUserExistApi = (email: string) => {
  const endpoint = `${API_ENDPOINTS.AUTH.checkEmailAlreadyRegister}${encodeURIComponent(email)}`;
  return httpClient(httpMethods.POST, endpoint);
};

export const validateBusinessEmailApi = (email: string) => {
  const endpoint = `${API_ENDPOINTS.AUTH.validateBussinessEmail}${encodeURIComponent(email)}`;
  return httpClient(httpMethods.POST, endpoint);
};

export const loginApi = (userData: any) => {
  return httpClient(httpMethods.POST, API_ENDPOINTS.AUTH.login, userData, {
    skipAuthRedirect: true,
  });
};

export const resetPassApi = (email: string) => {
  const endpoint = `${API_ENDPOINTS.AUTH.resetPassword}${encodeURIComponent(email)}`;
  return httpClient(httpMethods.GET, endpoint);
};

export const checkWebaddressAvailApi = (webaddress: any) => {
  const endpoint = `${API_ENDPOINTS.AUTH.checkWebaddressAvail}${encodeURIComponent(webaddress)}`;
  return httpClient(httpMethods.POST, endpoint);
};

export const checkCompanyNameApi = (name: string) => {
  const endpoint = `${API_ENDPOINTS.AUTH.checkCompanyNameAvail}${encodeURIComponent(name)}`;
  return httpClient(httpMethods.POST, endpoint);
};

export const createUserApi = (userData: {}) => {
  const endpoint = `${API_ENDPOINTS.AUTH.onboarding}`;
  return httpClient(httpMethods.POST, endpoint, userData);
};

export const resendEmailApi = (email: string) => {
  const encodedEmail = encodeURIComponent(email);
  const endpoint = `${API_ENDPOINTS.AUTH.resendEmail}?email=${encodedEmail}`;
  return httpClient(httpMethods.POST, endpoint);
};

export const sendOtpApi = (email: string) => {
  return httpClient(httpMethods.POST, API_ENDPOINTS.AUTH.sendOtp, { email });
};

export const verifyOtpApi = (email: string, otp: string) => {
  return httpClient(httpMethods.POST, API_ENDPOINTS.AUTH.verifyOtp, {
    email,
    otp,
  });
};

export const checkEmailVerifiedApi = (email: string) => {
  const endpoint = `${API_ENDPOINTS.AUTH.checkEmailVerified}${encodeURIComponent(email)}`;
  return httpClient(httpMethods.GET, endpoint, undefined, {
    skipAuthRedirect: true,
  });
};

export const checkAccountVerifiedApi = (email: string) => {
  const endpoint = `${API_ENDPOINTS.AUTH.checkAccountVerifed}${encodeURIComponent(email)}`;
  return httpClient(httpMethods.GET, endpoint, undefined, {
    skipAuthRedirect: true,
  });
};

export const getUserlist = (params?: { page?: number; size?: number }) => {
  const queryParts: string[] = [];
  if (params?.page !== undefined) queryParts.push(`page=${params.page}`);
  if (params?.size !== undefined) queryParts.push(`size=${params.size}`);
  const qs = queryParts.length ? `?${queryParts.join("&")}` : "";
  return httpClient(httpMethods.GET, `${API_ENDPOINTS.AUTH.getUsers}${qs}`);
};

export const getAdminUserlist = (params?: { page?: number; size?: number }) => {
  const queryParts: string[] = [];
  if (params?.page !== undefined) queryParts.push(`page=${params.page}`);
  if (params?.size !== undefined) queryParts.push(`size=${params.size}`);
  const qs = queryParts.length ? `?${queryParts.join("&")}` : "";
  return httpClient(
    httpMethods.GET,
    `${API_ENDPOINTS.AUTH.adminUserList}${qs}`,
  );
};

export const getUserRoles = () => {
  return httpClient(httpMethods.GET, API_ENDPOINTS.USER.getUserRoles);
};

export const getRolePermissions = (roleId: number) => {
  return httpClient(
    httpMethods.GET,
    API_ENDPOINTS.USER.getRolePermissions(roleId),
  );
};

export const getUserOrgs = () => {
  return httpClient(httpMethods.GET, API_ENDPOINTS.USER.getUserOrgs);
};

export const fetchAssignee = () => {
  const endpoint = API_ENDPOINTS.USER.assignee;
  return httpClient(httpMethods.GET, endpoint).then((res) => {
    return res;
  });
};
export const inviteUserApi = (userData: {}) => {
  const endpoint = `${API_ENDPOINTS.AUTH.user}`;
  return httpClient(httpMethods.POST, endpoint, userData);
};

export const bulkInviteUsersApi = (payload: {
  invites: Array<{
    email: string;
    firstName?: string;
    role?: string;
    projectMapping?: Array<{ projectId: number; roleId: number }>;
  }>;
}) => {
  return httpClient(httpMethods.POST, "user/bulk", payload);
};

export const resendInviteApi = (email: string) => {
  return httpClient(httpMethods.POST, "user/resendinvite", { email });
};

export const revokeInviteApi = (payload: {
  invitationId?: number;
  email?: string;
}) => {
  return httpClient(httpMethods.POST, "user/revokeinvite", payload);
};

// export const updateUserApi = (userData: {}) => {
//   const endpoint = `${API_ENDPOINTS.AUTH.user}`;
//   return httpClient(httpMethods.PUT, endpoint, userData);
// };

export const updateUserApi = (id: number, payload: any) => {
  return httpClient(
    httpMethods.PATCH,
    `${API_ENDPOINTS.AUTH.user}${id}`,
    payload,
  );
};

/** Update a user's role in a single project without touching other project memberships. */
export const updateUserProjectRoleApi = (
  userId: number,
  projectId: number,
  roleId: number,
) => {
  return httpClient(
    httpMethods.PATCH,
    `${API_ENDPOINTS.AUTH.user}${userId}/projects/${projectId}`,
    { roleId },
  );
};

/** Remove a user from a single project without affecting other project memberships. */
export const removeUserFromProjectApi = (userId: number, projectId: number) => {
  return httpClient(
    httpMethods.DELETE,
    `${API_ENDPOINTS.AUTH.user}${userId}/projects/${projectId}`,
  );
};

/** Self-service profile update — calls PATCH /user/me, no USERS:WRITE required. */
export const updateSelfApi = (payload: {
  firstName?: string;
  lastName?: string;
  phonenumber?: number | null;
}) => {
  return httpClient(httpMethods.PATCH, `${API_ENDPOINTS.AUTH.user}me`, payload);
};

export const fetchUserById = (id: number) => {
  const endpoint = `${API_ENDPOINTS.AUTH.user}${id}`;
  return httpClient(httpMethods.GET, endpoint);
};

export const deleteUserApi = (id: number) => {
  return httpClient(httpMethods.DELETE, `${API_ENDPOINTS.AUTH.user}${id}`);
};

export const getUserProjectRoles = () => {
  return httpClient(httpMethods.GET, "user/me/projects");
};

export const fetchProfileStats = () =>
  httpClient<{
    testCasesAuthored: number;
    openDefectsAssigned: number;
    atRiskCycles: number;
    openCycles: number;
    recentExecutions: Array<{
      testCaseId: number;
      testCaseName: string;
      cycleName: string;
      cycleId: number;
      status: string;
      executedAt: string | null;
    }>;
    retestCases: Array<{
      testCaseId: number;
      testCaseName: string;
      cycleId: number;
      cycleName: string;
      updatedAt: string;
    }>;
    releases: { onTrack: number; slipping: number; total: number };
  }>(httpMethods.GET, "user/me/profile-stats").then((res) => res.data);

// Logout API

export const logoutUser = () => {
  const endpoint = `${API_ENDPOINTS.AUTH.logout}`;
  return httpClient(httpMethods.GET, endpoint, undefined, {
    skipAuthRedirect: true,
  });
};

export const approveUserApi = (id: number) => {
  return httpClient(httpMethods.POST, API_ENDPOINTS.AUTH.approveUser(id));
};

export const fetchDemoRequestsApi = (params?: {
  page?: number;
  size?: number;
}) => {
  const queryParts: string[] = [];
  if (params?.page !== undefined) queryParts.push(`page=${params.page}`);
  if (params?.size !== undefined) queryParts.push(`size=${params.size}`);
  const qs = queryParts.length ? `?${queryParts.join("&")}` : "";
  return httpClient(httpMethods.GET, `${API_ENDPOINTS.AUTH.demoRequests}${qs}`);
};
